import React from 'react';

export interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'text' | 'number' | 'date';
  value: string;
  onChange: (value: string) => void;
  options?: { value: string; label: string }[];
  placeholder?: string;
  step?: string;
}

interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterConfig[];
  onClearFilters: () => void;
  totalCount: number;
  filteredCount: number;
  entityName: string;
  onAdd?: () => void;
  addLabel?: string;
  onExport?: () => void;
  onPrint?: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm, onSearchChange, searchPlaceholder = 'Search...',
  filters = [], onClearFilters,
  totalCount, filteredCount, entityName,
  onAdd, addLabel, onExport, onPrint,
}) => {
  const hasActiveFilters = searchTerm || filters.some(f =>
    f.type === 'select' ? f.value !== 'all' : f.value !== ''
  );

  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <h2 className="text-xl font-semibold text-gray-900">{entityName}</h2>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:w-64"
            />
            {onExport && (
              <button onClick={onExport} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">
                Export
              </button>
            )}
            {onPrint && (
              <button onClick={onPrint} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                Print
              </button>
            )}
            {onAdd && (
              <button onClick={onAdd} className="px-4 py-2 bg-ocean-600 text-white rounded-md hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-ocean-500">
                {addLabel || `Add ${entityName}`}
              </button>
            )}
          </div>
        </div>
      </div>

      {filters.length > 0 && (
        <div className="p-6 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filters.map((filter) => (
              <div key={filter.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{filter.label}</label>
                {filter.type === 'select' ? (
                  <select
                    value={filter.value}
                    onChange={(e) => filter.onChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {filter.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={filter.type}
                    value={filter.value}
                    onChange={(e) => filter.onChange(e.target.value)}
                    placeholder={filter.placeholder}
                    step={filter.step}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {filteredCount} of {totalCount} {entityName.toLowerCase()}
            </span>
            {hasActiveFilters && (
              <button
                onClick={onClearFilters}
                className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
